import { QRCodeCanvas } from 'qrcode.react';
import { Copy, RefreshCw, Link as LinkIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { env } from '@/lib/env';
import type { AccountantDTO } from '@/features/accountant/api';

interface Props {
  accountant: AccountantDTO;
  onGenerate: () => Promise<void>;
  onRotate: () => Promise<void>;
  isGenerating: boolean;
  isRotating: boolean;
}

function buildUrl(token: string): string {
  const base =
    typeof window !== 'undefined' ? window.location.origin : env.apiBaseUrl;
  return `${base.replace(/\/$/, '')}/contador/${token}`;
}

export function ReadonlyLinkCard({
  accountant,
  onGenerate,
  onRotate,
  isGenerating,
  isRotating,
}: Props) {
  const toast = useToast();
  const [rotateOpen, setRotateOpen] = useState(false);
  const url = useMemo(
    () => (accountant.readonlyToken ? buildUrl(accountant.readonlyToken) : null),
    [accountant.readonlyToken],
  );

  const expiresLabel = accountant.readonlyTokenExpiresAt
    ? new Date(accountant.readonlyTokenExpiresAt).toLocaleDateString('pt-BR')
    : null;

  const copy = async (): Promise<void> => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.show({ title: 'Link copiado.', variant: 'success' });
    } catch {
      toast.show({ title: 'Não foi possível copiar.', variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Link read-only</CardTitle>
        <p className="text-sm text-(--color-fg-muted)">
          O contador acessa o dashboard do mês sem login. Rotacione o token se suspeitar de vazamento.
        </p>
      </CardHeader>

      {url ? (
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-col gap-2 md:flex-1">
            <label className="text-xs font-medium text-(--color-fg-muted)" htmlFor="readonly-url">
              URL pública
            </label>
            <div className="flex gap-2">
              <input
                id="readonly-url"
                readOnly
                value={url}
                className="h-11 flex-1 rounded-md border border-(--color-border) bg-(--color-surface) px-3 text-sm text-(--color-fg) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button type="button" variant="secondary" onClick={copy} aria-label="Copiar link">
                <Copy className="mr-1 h-4 w-4" aria-hidden /> Copiar
              </Button>
            </div>
            {expiresLabel ? (
              <p className="text-xs text-(--color-fg-muted)">
                Expira em {expiresLabel}
              </p>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRotateOpen(true)}
                disabled={isRotating}
              >
                <RefreshCw className="mr-1 h-4 w-4" aria-hidden /> Rotacionar token
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-md border border-(--color-border) bg-white p-3">
            <QRCodeCanvas value={url} size={144} aria-label="QR code do link" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum link gerado. Clique para criar um token read-only válido por 90 dias.
          </p>
          <Button
            type="button"
            onClick={async () => {
              await onGenerate();
              toast.show({ title: 'Link gerado.', variant: 'success' });
            }}
            disabled={isGenerating}
          >
            <LinkIcon className="mr-1 h-4 w-4" aria-hidden /> Gerar link read-only
          </Button>
        </div>
      )}

      <Dialog open={rotateOpen} onOpenChange={setRotateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotacionar token</DialogTitle>
            <p className="text-sm text-(--color-fg-muted)">
              Um novo link será gerado e o atual deixará de funcionar imediatamente. Envie a nova URL ao contador.
            </p>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setRotateOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isRotating}
              onClick={async () => {
                await onRotate();
                setRotateOpen(false);
                toast.show({ title: 'Token rotacionado.', variant: 'success' });
              }}
            >
              Rotacionar agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
